import { useState, useCallback, useRef, useEffect, useId } from "react";

declare global {
    interface Window {
        YT: {
            Player: new (
                element: string | HTMLElement,
                config: {
                    videoId: string;
                    playerVars?: Record<string, unknown>;
                    events?: {
                        onReady?: (event: { target: YTPlayer }) => void;
                        onStateChange?: (event: { data: number; target: YTPlayer }) => void;
                    };
                }
            ) => YTPlayer;
            PlayerState: {
                PLAYING: number;
                PAUSED: number;
                ENDED: number;
                BUFFERING: number;
            };
        };
        onYouTubeIframeAPIReady?: () => void;
    }
}

interface YTPlayer {
    playVideo: () => void;
    pauseVideo: () => void;
    seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
    getCurrentTime: () => number;
    getDuration: () => number;
    getPlayerState: () => number;
    setPlaybackRate: (rate: number) => void;
    getPlaybackRate: () => number;
    mute: () => void;
    unMute: () => void;
    unloadModule: (module: string) => void;
    destroy: () => void;
}

interface UseYouTubePlayerOptions {
    onTimeUpdate?: (time: number) => void;
    onStateChange?: (isPlaying: boolean) => void;
    /**
     * Start the embed at this offset and let it play on its own.
     *
     * This is handed to the *build* (`playerVars.start` + `autoplay: 1`), not
     * applied afterwards with `seekTo`. A clip is a moment, and seeking a
     * freshly ready player means: load at 0:00, wait for `onReady`, seek,
     * buffer again at the new offset, then play. That second stall is several
     * seconds long on a long video, and a learner pressing "next" watches a
     * frozen poster through it. Building at the offset fetches and starts the
     * clip in one pass.
     */
    startAt?: number | null;
    /**
     * Build the player muted, so it may start without a gesture.
     *
     * A muted player is the only one a browser lets start on its own, which is
     * what makes warming the next clip possible: it buffers at its line while
     * the current one plays, and is unmuted when it is promoted.
     */
    startMuted?: boolean;
}

export function useYouTubePlayer(
    videoId: string | null,
    options: UseYouTubePlayerOptions = {}
) {
    const { onTimeUpdate, onStateChange, startAt, startMuted } = options;
    const [isReady, setIsReady] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    /**
     * Which build is ready, and where that build started. Read at build time
     * from a ref so a new clip start never rebuilds the player on its own —
     * only a new video does.
     */
    const [buildInfo, setBuildInfo] = useState<{ token: number; start: number | null }>({
        token: 0,
        start: null,
    });
    const startAtRef = useRef<number | null>(null);
    startAtRef.current = startAt != null && Number.isFinite(startAt) ? startAt : null;
    const startMutedRef = useRef(false);
    startMutedRef.current = startMuted === true;
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const playerRef = useRef<YTPlayer | null>(null);
    const timeUpdateIntervalRef = useRef<number | null>(null);
    // `useId` rather than a timestamp: two players can be mounted in the same
    // millisecond for the same page (the visible clip and the one warming the
    // next), and a shared container id would hand both to one <div>.
    const reactId = useId();
    const containerIdRef = useRef<string>(
        `youtube-player-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`
    );

    // Use refs for callbacks to prevent re-initialization when they change
    const onTimeUpdateRef = useRef(onTimeUpdate);
    const onStateChangeRef = useRef(onStateChange);

    // Keep refs in sync with latest callbacks
    useEffect(() => {
        onTimeUpdateRef.current = onTimeUpdate;
    }, [onTimeUpdate]);

    useEffect(() => {
        onStateChangeRef.current = onStateChange;
    }, [onStateChange]);

    const startTimeUpdateInterval = useCallback(() => {
        if (timeUpdateIntervalRef.current) return;

        timeUpdateIntervalRef.current = window.setInterval(() => {
            if (playerRef.current) {
                const time = playerRef.current.getCurrentTime();
                setCurrentTime(time);
                onTimeUpdateRef.current?.(time);
            }
        }, 50); // Update every 50ms for smooth caption sync
    }, []);

    const stopTimeUpdateInterval = useCallback(() => {
        if (timeUpdateIntervalRef.current) {
            clearInterval(timeUpdateIntervalRef.current);
            timeUpdateIntervalRef.current = null;
        }
    }, []);

    // Initialize player when API is ready
    const initPlayer = useCallback(() => {
        if (!videoId || !window.YT || !window.YT.Player) return;

        // Wait for container to be in DOM
        const container = document.getElementById(containerIdRef.current);
        if (!container) {
            setTimeout(initPlayer, 100);
            return;
        }

        // Destroy existing player if any
        if (playerRef.current) {
            playerRef.current.destroy();
        }

        // The player is built inside a throwaway child of React's container,
        // never on the container itself: `new YT.Player(el)` *replaces* `el`
        // with its iframe, so React's tree then pointed at a node that no
        // longer existed and the next render crashed the page with
        // "removeChild … is not a child of this node" — which is what stopped
        // next/previous from ever playing a second clip.
        container.replaceChildren();
        const mount = document.createElement("div");
        mount.className = "h-full w-full";
        container.appendChild(mount);

        // A new player starts unready. Without this, switching videos kept
        // `isReady` true from the previous instance, so anything waiting on the
        // ready transition (see `startTime` in MultiPlatformPlayer) fired against
        // a half-built player and was silently dropped.
        setIsReady(false);
        // Same trap for playback: a stale `true` kept the transport showing
        // pause over a player that had not started yet.
        setIsPlaying(false);

        const start = startAtRef.current != null ? Math.floor(startAtRef.current) : null;

        playerRef.current = new window.YT.Player(mount, {
            videoId,
            playerVars: {
                // A clip player is built already sitting on the line it has to
                // play, so the browser starts fetching that part of the video
                // instead of 0:00 and no second seek is needed after `onReady`.
                ...(start != null ? { start, autoplay: 1 } : { autoplay: 0 }),
                // Muted playback is always permitted, which is how a warming
                // player is allowed to start on its own.
                ...(startMutedRef.current ? { mute: 1 } : {}),
                controls: 1,
                modestbranding: 1,
                rel: 0,
                // Off on purpose: the page draws its own live caption over the
                // player, and two sets of subtitles showing the same words in
                // slightly different cuts is unusable.
                cc_load_policy: 0,
            },
            events: {
                onReady: (event) => {
                    // Kill the embed's captions for real.
                    //
                    // `cc_load_policy: 0` below only stops the API *forcing* them
                    // on — if the viewer's own YouTube cookie has captions
                    // enabled, the embed still renders them, and they sat as a
                    // ghost line behind our overlay. Unloading the module is the
                    // only switch that removes them regardless of that
                    // preference. It also removes YouTube's CC button, which is
                    // correct: this page owns the caption text now.
                    //
                    // Re-applied on every ready because each video builds a new
                    // player instance.
                    try {
                        event.target.unloadModule("captions");
                    } catch {
                        // Older/edge players may not expose the module; the
                        // overlay above it still works.
                    }

                    // The token is what tells "this ready player" from "the one
                    // before it": a caller keying its work on it re-applies a
                    // clip to a rebuilt player even when the video and the
                    // start second are identical.
                    setBuildInfo((info) => ({ token: info.token + 1, start }));
                    setIsReady(true);
                    setDuration(event.target.getDuration());
                    startTimeUpdateInterval();
                },
                onStateChange: (event) => {
                    const playing = event.data === window.YT.PlayerState.PLAYING;

                    // `onReady` is not enough on its own: the embed can restore
                    // the viewer's caption preference when playback starts, which
                    // put YouTube's line back on screen underneath ours. Re-applied
                    // here so the only captions on the page are the page's own.
                    if (playing) {
                        try {
                            event.target.unloadModule("captions");
                        } catch {
                            // Module unavailable on this player; harmless.
                        }
                    }

                    setIsPlaying(playing);
                    onStateChangeRef.current?.(playing);

                    if (playing) {
                        startTimeUpdateInterval();
                    } else {
                        stopTimeUpdateInterval();
                    }
                },
            },
        });
    }, [videoId, startTimeUpdateInterval, stopTimeUpdateInterval]);

    // Load YouTube IFrame API
    useEffect(() => {
        if (!videoId) return;

        // Check if API is already loaded
        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            // Load the API script - only if not already loading/loaded
            const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
            if (!existingScript) {
                const tag = document.createElement("script");
                tag.src = "https://www.youtube.com/iframe_api";
                const firstScriptTag = document.getElementsByTagName("script")[0];
                firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
            }

            // Set up the callback
            const originalCallback = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                originalCallback?.();
                initPlayer();
            };
        }

        // Returned on both paths on purpose: when the API was already loaded
        // this effect used to skip its cleanup, so clearing the clip left the
        // iframe playing under a removed container and the 50ms time poll
        // running for the rest of the session.
        return () => {
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
            if (timeUpdateIntervalRef.current) {
                clearInterval(timeUpdateIntervalRef.current);
                timeUpdateIntervalRef.current = null;
            }
        };
    }, [videoId, initPlayer]);

    /**
     * `new YT.Player(...)` returns an object with **no methods** until `onReady`
     * fires — the API hangs them on once the iframe is live. So `playerRef.current`
     * being non-null does not mean it is usable, and calling straight into it
     * throws "seekTo is not a function".
     *
     * That is not hypothetical: switching clips rebuilds the player, and a loop
     * seek or a playback-rate change arriving in that window used to crash the
     * whole page. Every command goes through this guard.
     */
    const readyPlayer = useCallback((): YTPlayer | null => {
        const player = playerRef.current as YTPlayer | null;
        return player && typeof player.seekTo === "function" ? player : null;
    }, []);

    const play = useCallback(() => {
        readyPlayer()?.playVideo();
    }, [readyPlayer]);

    const pause = useCallback(() => {
        readyPlayer()?.pauseVideo();
    }, [readyPlayer]);

    /**
     * Sound is restored only after a warm player has started playing: the play
     * itself is then the muted, always-permitted one, and unmuting a playing
     * element does not ask the browser for another permission.
     */
    const mute = useCallback(() => {
        readyPlayer()?.mute();
    }, [readyPlayer]);

    const unMute = useCallback(() => {
        readyPlayer()?.unMute();
    }, [readyPlayer]);

    const seekTo = useCallback((seconds: number) => {
        const player = readyPlayer();
        if (!player) return false;
        player.seekTo(seconds, true);
        setCurrentTime(seconds);
        return true;
    }, [readyPlayer]);

    // Shadowing is the reason this exists: a learner repeats a line at 0.75x
    // until it lands, then moves on. 1x, 0.75x and 0.5x are the only steps the
    // UI offers, but the player accepts anything.
    const setPlaybackRate = useCallback((rate: number) => {
        readyPlayer()?.setPlaybackRate(rate);
    }, [readyPlayer]);

    const getPlaybackRate = useCallback(() => {
        return readyPlayer()?.getPlaybackRate() ?? 1;
    }, [readyPlayer]);

    const toggle = useCallback(() => {
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    }, [isPlaying, play, pause]);

    return {
        containerId: containerIdRef.current,
        isReady,
        isPlaying,
        currentTime,
        duration,
        /** Increments for every player that reports ready. */
        buildToken: buildInfo.token,
        /** The second the current build started at (null when built at 0:00). */
        builtStart: buildInfo.start,
        play,
        pause,
        mute,
        unMute,
        toggle,
        seekTo,
        setPlaybackRate,
        getPlaybackRate,
    };
}
